import { useCallback, useEffect, useState } from 'react';
import { BarChart3, RefreshCw } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

type SalesInvoice = {
  id: string;
  invoiceNo: string;
  status: string;
  date: string;
  customerName: string;
  jobNo: string;
  currency: string;
  totalAmount: number;
  paidAmount: number;
  openAmount: number;
};

type SalesReport = {
  period: { from: string; to: string };
  totals: {
    invoiceCount: number;
    totalSales: number;
    totalPaid: number;
    totalOpen: number;
  };
  invoices: SalesInvoice[];
};

function thisYear() {
  const now = new Date();
  return { from: `${now.getFullYear()}-01-01`, to: now.toISOString().split('T')[0]! };
}

function money(value: number, currency = 'SGD') {
  return new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

export default function SalesReportPage() {
  const [from, setFrom] = useState(thisYear().from);
  const [to, setTo] = useState(thisYear().to);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SalesReport | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<SalesReport>(`/finance/reports/sales?from=${from}&to=${to}`);
      setData(res);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <BarChart3 className="h-6 w-6 text-blue-600" />
          <div>
            <h1 className="text-xl font-semibold">Sales Report</h1>
            <p className="text-sm text-muted-foreground">AR invoice sales only. AP bills are excluded.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input className="rounded border px-2 py-1 text-sm" type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
          <input className="rounded border px-2 py-1 text-sm" type="date" value={to} onChange={(event) => setTo(event.target.value)} />
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <Card><CardContent className="p-3"><div className="text-xs text-muted-foreground">Invoices</div><div className="mt-1 text-lg font-bold">{data?.totals.invoiceCount ?? 0}</div></CardContent></Card>
        <Card><CardContent className="p-3"><div className="text-xs text-muted-foreground">Sales</div><div className="mt-1 text-lg font-bold text-blue-700">{money(data?.totals.totalSales ?? 0)}</div></CardContent></Card>
        <Card><CardContent className="p-3"><div className="text-xs text-muted-foreground">Paid</div><div className="mt-1 text-lg font-bold text-emerald-700">{money(data?.totals.totalPaid ?? 0)}</div></CardContent></Card>
        <Card><CardContent className="p-3"><div className="text-xs text-muted-foreground">Open</div><div className="mt-1 text-lg font-bold text-amber-700">{money(data?.totals.totalOpen ?? 0)}</div></CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center text-muted-foreground">Loading...</div>
          ) : !data || data.invoices.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">No AR invoice sales for this period.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>No</TableHead>
                  <TableHead>Job No</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Sales</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Open</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell className="font-mono">{invoice.invoiceNo}</TableCell>
                    <TableCell className="font-mono text-xs">{invoice.jobNo || '-'}</TableCell>
                    <TableCell>{invoice.customerName}</TableCell>
                    <TableCell>{invoice.status}</TableCell>
                    <TableCell>{String(invoice.date || '').slice(0, 10)}</TableCell>
                    <TableCell className="text-right">{money(invoice.totalAmount, invoice.currency)}</TableCell>
                    <TableCell className="text-right">{money(invoice.paidAmount, invoice.currency)}</TableCell>
                    <TableCell className="text-right">{money(invoice.openAmount, invoice.currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
